import { useTranslation } from 'react-i18next';
import { Link } from '@inertiajs/react';
import { cn } from '../../lib/cn';

function useActivityTypeShort() {
  const { t } = useTranslation();
  return {
    reading: t('lessonFlow.read'),
    live_session: t('lessonFlow.live'),
    essay: t('lessonFlow.essay'),
    assessment: t('lessonFlow.assess'),
    vocab_practice: t('lessonFlow.vocab'),
    presentation: t('lessonFlow.pres'),
    activity: t('common.activity'),
  };
}

// Mini icons (w-2.5 h-2.5) for pill display
const MiniIcons = {
  reading: (p) => (
    <svg {...p} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
      <path d="M12 7v14" /><path d="M3 18a1 1 0 0 1-1-1V4a1 1 0 0 1 1-1h5a4 4 0 0 1 4 4 4 4 0 0 1 4-4h5a1 1 0 0 1 1 1v13a1 1 0 0 1-1 1h-6a3 3 0 0 0-3 3 3 3 0 0 0-3-3z" />
    </svg>
  ),
  live_session: (p) => (
    <svg {...p} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
      <rect width="20" height="14" x="2" y="3" rx="2" /><path d="M7 21h10" />
    </svg>
  ),
  essay: (p) => (
    <svg {...p} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
      <rect width="18" height="18" x="3" y="3" rx="2" /><path d="M12 12H9.5a2.5 2.5 0 0 1 0-5H17" /><path d="M12 7v10" /><path d="M16 7v10" />
    </svg>
  ),
  assessment: (p) => (
    <svg {...p} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
      <path d="M16 4h2a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h2" /><rect x="8" y="2" width="8" height="4" rx="1" /><path d="m9 14 2 2 4-4" />
    </svg>
  ),
  vocab_practice: (p) => (
    <svg {...p} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
      <rect x="4" y="4" width="16" height="16" rx="2" /><rect x="8" y="2" width="8" height="4" rx="1" /><rect x="8" y="18" width="8" height="4" rx="1" />
    </svg>
  ),
  presentation: (p) => (
    <svg {...p} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
      <path d="M2 3h20" /><path d="M21 3v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V3" /><path d="m7 21 5-5 5 5" />
    </svg>
  ),
  activity: (p) => (
    <svg {...p} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
      <path d="M22 17a2 2 0 0 1-2 2H6.828a2 2 0 0 0-1.414.586l-2.202 2.202A.71.71 0 0 1 2 21.286V5a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2z" />
    </svg>
  ),
};

function MiniIcon({ activityType, className }) {
  const Renderer = MiniIcons[activityType] || MiniIcons.activity;
  return Renderer({ className });
}

const ChevronLeft = () => (
  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
  </svg>
);

const ChevronRight = () => (
  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
  </svg>
);

const SmallChevron = () => (
  <svg className="w-2.5 h-2.5 text-gray-300 dark:text-gray-600 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M9 5l7 7-7 7" />
  </svg>
);

export default function CompactLessonFlow({ lessonContext }) {
  const { t } = useTranslation();
  const ACTIVITY_TYPE_SHORT = useActivityTypeShort();
  const activities = lessonContext.activities || [];

  const priorHW = activities.filter(a => a.slot === 'prior_homework');
  const main = activities.filter(a => a.slot === 'main');
  const hw = activities.filter(a => a.slot === 'homework');

  const prevLessonPath = lessonContext.prev_lesson
    ? `/teacher/courses/${lessonContext.course_id}/units/${lessonContext.prev_lesson.unit_id || lessonContext.unit_id}/lessons/${lessonContext.prev_lesson.id}`
    : null;
  const nextLessonPath = lessonContext.next_lesson
    ? `/teacher/courses/${lessonContext.course_id}/units/${lessonContext.next_lesson.unit_id || lessonContext.unit_id}/lessons/${lessonContext.next_lesson.id}`
    : null;

  const pills = [];

  // Prior HW section
  if (!lessonContext.is_first) {
    if (priorHW.length > 0) {
      pills.push({ type: 'label', label: t('lessonFlow.priorHw'), key: 'prior_hw_label' });
      priorHW.forEach(a => pills.push({ type: 'activity', activity: a, key: `a-${a.slot}-${a.title}` }));
    } else {
      pills.push({ type: 'empty_label', label: t('lessonFlow.priorHw'), key: 'prior_hw_label' });
    }
  }

  // Main flow activities
  main.forEach(a => pills.push({ type: 'activity', activity: a, key: `a-main-${a.title}` }));

  // Homework section
  if (hw.length > 0) {
    pills.push({ type: 'label', label: t('lessonFlow.hw'), key: 'hw_label' });
    hw.forEach(a => pills.push({ type: 'activity', activity: a, key: `a-hw-${a.title}` }));
  } else {
    pills.push({ type: 'empty_label', label: t('lessonFlow.hw'), key: 'hw_label' });
  }

  return (
    <div className="mb-4 text-center">
      <Link
        href={lessonContext.from_lesson || '#'}
        className="text-xs font-medium text-gray-500 dark:text-gray-400 hover:text-blue-600 dark:hover:text-blue-400 transition-colors"
      >
        {lessonContext.title}
      </Link>
      <div className="flex items-center justify-center gap-1 mt-1.5 flex-wrap">
        {prevLessonPath ? (
          <Link href={prevLessonPath} className="text-gray-400 hover:text-gray-600 dark:text-gray-500 dark:hover:text-gray-300 transition-colors flex-shrink-0">
            <ChevronLeft />
          </Link>
        ) : (
          <span className="text-gray-200 dark:text-gray-700 flex-shrink-0">
            <ChevronLeft />
          </span>
        )}

        {pills.map((pill, i) => (
          <div key={pill.key} className="flex items-center gap-1">
            {i > 0 && <SmallChevron />}
            {pill.type === 'label' && (
              <span className="text-[10px] leading-none whitespace-nowrap font-semibold text-gray-500 dark:text-gray-400 px-1.5 py-0.5 rounded bg-gray-100 dark:bg-gray-800">
                {pill.label}
              </span>
            )}
            {pill.type === 'empty_label' && (
              <span className="text-[10px] leading-none whitespace-nowrap text-gray-400 dark:text-gray-500 px-1.5 py-0.5 rounded border border-dashed border-gray-200 dark:border-gray-700">
                {pill.label}
              </span>
            )}
            {pill.type === 'activity' && (() => {
              const isAssessment = pill.activity.slottable_type === 'Assessment';
              const iconType = isAssessment ? 'assessment' : (pill.activity.activity_type || 'activity');
              const label = isAssessment
                ? t('lessonFlow.assessment')
                : (ACTIVITY_TYPE_SHORT[pill.activity.activity_type] || pill.activity.activity_type || t('common.activity'));
              return (
                <span className="inline-flex items-center gap-1 text-[10px] leading-none whitespace-nowrap font-medium px-1.5 py-0.5 rounded border border-gray-300 bg-gray-50 text-gray-700 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-300">
                  <MiniIcon
                    activityType={iconType}
                    className="w-2.5 h-2.5 flex-shrink-0 text-gray-400 dark:text-gray-500"
                  />
                  {label}
                </span>
              );
            })()}
          </div>
        ))}

        {nextLessonPath ? (
          <Link href={nextLessonPath} className="text-gray-400 hover:text-gray-600 dark:text-gray-500 dark:hover:text-gray-300 transition-colors flex-shrink-0">
            <ChevronRight />
          </Link>
        ) : (
          <span className="text-gray-200 dark:text-gray-700 flex-shrink-0">
            <ChevronRight />
          </span>
        )}
      </div>
    </div>
  );
}
